import type { CrudSearchItem, CrudTableColumn } from '@/types/crud-config'
import type { PreSalesBuyingHistoryRecord } from './pre-sales-buying-history.service'

const yesNoOptions = [
  { label: '是', value: '是' },
  { label: '否', value: '否' }
]

export const paymentChannelLabels: Record<string, string> = {
  wallet: '钱包',
  mock: '模拟支付',
  alipay_qr: '支付宝扫码支付',
  alipay_bar: '支付宝条码支付',
  alipay_app: '支付宝 App 支付',
  alipay_wap: '支付宝 Wap 网站支付',
  alipay_pc: '支付宝 PC 网站支付',
  wx_bar: '微信条码支付',
  wx_native: '微信扫码支付',
  wx_app: '微信 App 支付',
  wx_lite: '微信小程序支付',
  wx_pub: '微信公众号支付',
  wx_wap: '微信 Wap 网站支付'
}

export const paymentStatusLabels: Record<string, string> = {
  '0': '等待支付',
  '10': '支付成功',
  '20': '已退款',
  '30': '支付关闭',
  '40': '失败（三方订单不存在）',
  '50': '已过期（超过一周未完成支付）'
}

const paymentChannelOptions = Object.entries(paymentChannelLabels).map(
  ([value, label]) => ({ label, value })
)
const paymentStatusOptions = Object.entries(paymentStatusLabels).map(
  ([value, label]) => ({ label, value })
)

export const preSalesBuyingHistorySearchItems: CrudSearchItem[] = [
  {
    label: '商品名',
    prop: 'productName',
    type: 'input',
    placeholder: '请输入商品名'
  },
  {
    label: '客户姓名',
    prop: 'customerName',
    type: 'input',
    placeholder: '请输入客户姓名'
  },
  {
    label: '微信昵称',
    prop: 'nickname',
    type: 'input',
    placeholder: '请输入微信昵称'
  },
  {
    label: '客户unionid',
    prop: 'unionid',
    type: 'input',
    placeholder: '请输入客户unionid'
  },
  {
    label: '所属员工',
    prop: 'empName',
    type: 'input',
    placeholder: '请输入所属员工'
  },
  {
    label: '所属部门',
    prop: 'deptName',
    type: 'input',
    placeholder: '请输入所属部门'
  },
  {
    label: '直播间名称',
    prop: 'channelName',
    type: 'input',
    placeholder: '请输入直播间名称'
  },
  {
    label: '是否已签约',
    prop: 'isSigned',
    type: 'select',
    clearable: true,
    placeholder: '请选择是否已签约',
    options: yesNoOptions
  },
  {
    label: '支付单号',
    prop: 'tradeNo',
    type: 'input',
    placeholder: '请输入支付单号'
  },
  {
    label: '支付渠道',
    prop: 'channelCode',
    type: 'select',
    clearable: true,
    placeholder: '请选择支付渠道',
    options: paymentChannelOptions
  },
  {
    label: '支付状态',
    prop: 'status',
    type: 'select',
    clearable: true,
    placeholder: '请选择支付状态',
    options: paymentStatusOptions
  },
  {
    label: '是否退款',
    prop: 'isRefund',
    type: 'select',
    clearable: true,
    placeholder: '请选择是否退款',
    options: yesNoOptions
  },
  {
    label: '购买时间',
    prop: 'payTime',
    type: 'dateRange',
    valueFormat: 'YYYY-MM-DD',
    startPlaceholder: '开始时间',
    endPlaceholder: '结束时间',
    disabledDate: (date) => date.getTime() > Date.now()
  }
]

const formatMoney = (value: number) => `￥${(value / 100).toFixed(2)}`

export const preSalesBuyingHistoryTableColumns: CrudTableColumn<PreSalesBuyingHistoryRecord>[] =
  [
    {
      prop: 'customerName',
      label: '客户姓名',
      minWidth: 100,
      showOverflowTooltip: true
    },
    {
      prop: 'nickname',
      label: '微信昵称',
      minWidth: 100,
      showOverflowTooltip: true
    },
    {
      prop: 'unionid',
      label: '客户unionid',
      minWidth: 140,
      showOverflowTooltip: true
    },
    {
      prop: 'channelName',
      label: '直播间名称',
      minWidth: 120,
      showOverflowTooltip: true
    },
    {
      prop: 'productName',
      label: '商品名',
      minWidth: 120,
      showOverflowTooltip: true
    },
    {
      prop: 'price',
      label: '支付金额',
      minWidth: 100,
      formatter: (row) => formatMoney(row.price)
    },
    { prop: 'signUrl', label: '合同链接', minWidth: 100, slot: 'signUrl' },
    {
      prop: 'isSigned',
      label: '是否已签约',
      minWidth: 100,
      formatter: (row) => (row.signUrl ? row.isSigned : '-')
    },
    {
      prop: 'deptName',
      label: '所属部门',
      minWidth: 100,
      showOverflowTooltip: true
    },
    {
      prop: 'empName',
      label: '所属员工',
      minWidth: 100,
      showOverflowTooltip: true
    },
    { prop: 'payTime', label: '购买时间', minWidth: 150 },
    {
      prop: 'tradeNo',
      label: '支付单号',
      minWidth: 150,
      showOverflowTooltip: true
    },
    {
      prop: 'orderId',
      label: '支付ID',
      minWidth: 150,
      showOverflowTooltip: true
    },
    {
      prop: 'status',
      label: '支付状态',
      minWidth: 130,
      formatter: (row) => paymentStatusLabels[String(row.status)]
    },
    {
      prop: 'channelCode',
      label: '支付渠道',
      minWidth: 150,
      formatter: (row) => paymentChannelLabels[row.channelCode]
    },
    { prop: 'isRefund', label: '是否退款', minWidth: 100 },
    {
      prop: 'refundAmount',
      label: '退款金额',
      minWidth: 100,
      formatter: (row) => formatMoney(row.refundAmount)
    }
  ]
